import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, CardHeader, CardTitle, Col, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { ArrowDown, ArrowLeft, ArrowUp, ChevronsDown, ChevronsUp, Menu, Plus, Search, X } from 'react-feather';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toast from 'react-hot-toast';
import ReorderPlaylistComponent from '@/v2/components/Playlist/ReorderPlaylistComponent';
import { ImageUploader } from '@/atomicComponent/AtomicComponents';
import api from '@/data/api';
import { featuredPlaylistAdminApi, getApiErrorMessage } from '@/data/api/featuredPlaylistAdminApi';
import { ReleaseBadge } from './FeaturedPlaylistManager';
import {
	defaultScheduledPickerDate,
	formatDuration,
	formatReleaseET,
	isReleased,
	pickerDateToUtcIso,
	utcToPickerDate
} from './releaseTime';
import './FeaturedPlaylists.css';

const LIST_PATH = '/AdminDashboard/featured-playlist';

function VideoThumb({ video }) {
	return video.thumnailLink ? (
		<img className="fpl-vthumb" src={video.thumnailLink} alt="" loading="lazy" />
	) : (
		<div className="fpl-vthumb fpl-thumb--empty" />
	);
}

function VideoMeta({ video }) {
	return (
		<div className="fpl-vmeta">
			{video.instructor && <span>{video.instructor}</span>}
			<span>{formatDuration(video.duration)}</span>
			{video.releaseDateTime && !isReleased(video.releaseDateTime) && (
				<Badge color="warning" className="text-dark" title="This video itself is not released yet">
					Video releases {formatReleaseET(video.releaseDateTime)}
				</Badge>
			)}
			{video.unavailable && (
				<Badge color="danger" title="This video was deleted or deactivated. Remove it from the playlist.">
					Unavailable
				</Badge>
			)}
		</div>
	);
}

function FeaturedPlaylistEditor() {
	const { id } = useParams();
	const isEdit = Boolean(id);
	const navigate = useNavigate();

	const [loading, setLoading] = useState(isEdit);
	const [loadError, setLoadError] = useState('');
	const [saving, setSaving] = useState(false);
	const [errors, setErrors] = useState({});

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [thumbnail, setThumbnail] = useState(''); // existing URL string, a new File, or ''
	const [scheduled, setScheduled] = useState(false);
	const [releaseDate, setReleaseDate] = useState(defaultScheduledPickerDate());
	const [originalRelease, setOriginalRelease] = useState(null);
	const [videos, setVideos] = useState([]);

	const [query, setQuery] = useState('');
	const [results, setResults] = useState([]);
	const [searching, setSearching] = useState(false);

	// Load the playlist when editing.
	useEffect(() => {
		if (!isEdit) return;
		let cancelled = false;
		(async () => {
			try {
				const p = await featuredPlaylistAdminApi.get(id);
				if (cancelled) return;
				setName(p.name);
				setDescription(p.description);
				setThumbnail(p.thumbnailLink || '');
				setVideos(p.videos || []);
				setOriginalRelease(p.releaseDateTime);
				if (p.releaseDateTime) {
					setScheduled(true);
					setReleaseDate(utcToPickerDate(p.releaseDateTime));
				}
			} catch (error) {
				if (!cancelled) setLoadError(getApiErrorMessage(error, 'Could not load playlist'));
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [id, isEdit]);

	// Debounced video search. An empty query lists the most recent videos.
	useEffect(() => {
		let cancelled = false;
		setSearching(true);
		const t = setTimeout(async () => {
			try {
				const data = await featuredPlaylistAdminApi.searchVideos(query.trim());
				if (!cancelled) setResults(data);
			} catch (error) {
				if (!cancelled) toast.error(getApiErrorMessage(error, 'Video search failed'), { position: 'top-center' });
			} finally {
				if (!cancelled) setSearching(false);
			}
		}, 300);
		return () => {
			cancelled = true;
			clearTimeout(t);
		};
	}, [query]);

	const selectedIds = useMemo(() => new Set(videos.map(v => v._id)), [videos]);
	const totalDuration = useMemo(() => videos.reduce((sum, v) => sum + (Number(v.duration) || 0), 0), [videos]);
	const releaseIso = scheduled && releaseDate ? pickerDateToUtcIso(releaseDate) : null;

	const addVideo = video => {
		if (selectedIds.has(video._id)) return;
		setVideos(prev => [...prev, video]);
		setErrors(e => ({ ...e, videos: undefined }));
	};
	const removeVideo = videoId => setVideos(prev => prev.filter(v => v._id !== videoId));
	const moveVideo = (from, to) => {
		if (to < 0 || to >= videos.length || from === to) return;
		setVideos(prev => {
			const next = [...prev];
			const [item] = next.splice(from, 1);
			next.splice(to, 0, item);
			return next;
		});
	};

	const validate = () => {
		const next = {};
		if (!name.trim()) next.name = 'Name is required';
		if (videos.length === 0) next.videos = 'Add at least one video';
		if (scheduled && !releaseDate) next.release = 'Pick a release date and time';
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const onSave = async () => {
		if (!validate()) return;
		setSaving(true);
		try {
			let thumbnailLink = typeof thumbnail === 'string' ? thumbnail : '';
			if (thumbnail instanceof File) {
				const upload = await api.fileUploadApi(thumbnail);
				if (!upload?.data?.cdnLink) throw new Error(upload?.message || 'Thumbnail upload failed');
				thumbnailLink = upload.data.cdnLink;
			}
			const payload = {
				name: name.trim(),
				description: description.trim(),
				thumbnailLink,
				videoIds: videos.map(v => v._id),
				releaseDateTime: releaseIso
			};
			if (isEdit) await featuredPlaylistAdminApi.update(id, payload);
			else await featuredPlaylistAdminApi.create(payload);
			toast.success(isEdit ? 'Playlist updated' : 'Playlist created', { position: 'top-center' });
			navigate(LIST_PATH);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not save playlist'), { position: 'top-center' });
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<div className="text-center py-5">
				<Spinner />
			</div>
		);
	}

	if (loadError) {
		return (
			<Alert color="danger">
				{loadError}{' '}
				<Button color="link" className="p-0 align-baseline" onClick={() => navigate(LIST_PATH)}>
					Back to playlists
				</Button>
			</Alert>
		);
	}

	const renderSelected = (video, index) => (
		<div className={`fpl-vrow ${video.unavailable ? 'fpl-vrow--bad' : ''}`} key={video._id}>
			<span className="fpl-drag" title="Drag to reorder">
				<Menu size={16} />
			</span>
			<span className="fpl-position">{index + 1}</span>
			<VideoThumb video={video} />
			<div className="fpl-main">
				<div className="fpl-vtitle">{video.title || '(untitled video)'}</div>
				<VideoMeta video={video} />
			</div>
			<div className="fpl-actions">
				<ButtonGroup size="sm">
					<Button outline color="secondary" disabled={index === 0} onClick={() => moveVideo(index, 0)} title="Move to top">
						<ChevronsUp size={14} />
					</Button>
					<Button outline color="secondary" disabled={index === 0} onClick={() => moveVideo(index, index - 1)} title="Move up">
						<ArrowUp size={14} />
					</Button>
					<Button
						outline
						color="secondary"
						disabled={index === videos.length - 1}
						onClick={() => moveVideo(index, index + 1)}
						title="Move down"
					>
						<ArrowDown size={14} />
					</Button>
					<Button
						outline
						color="secondary"
						disabled={index === videos.length - 1}
						onClick={() => moveVideo(index, videos.length - 1)}
						title="Move to bottom"
					>
						<ChevronsDown size={14} />
					</Button>
				</ButtonGroup>
				<Button size="sm" outline color="danger" onClick={() => removeVideo(video._id)} title="Remove from playlist">
					<X size={14} />
				</Button>
			</div>
		</div>
	);

	return (
		<div className="fpl-editor">
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
				<Button color="link" className="px-0" onClick={() => navigate(LIST_PATH)}>
					<ArrowLeft size={16} className="me-1" />
					All featured playlists
				</Button>
				<div className="d-flex gap-2">
					<Button color="secondary" outline onClick={() => navigate(LIST_PATH)} disabled={saving}>
						Cancel
					</Button>
					<Button color="primary" onClick={onSave} disabled={saving}>
						{saving ? <Spinner size="sm" /> : isEdit ? 'Save changes' : 'Create playlist'}
					</Button>
				</div>
			</div>

			<Row className="g-3">
				<Col lg="5">
					<Card className="fpl-card">
						<CardHeader>
							<CardTitle tag="h4" className="mb-0">
								{isEdit ? 'Edit featured playlist' : 'New featured playlist'}
							</CardTitle>
						</CardHeader>
						<CardBody>
							<FormGroup>
								<Label for="fpl-name">Name</Label>
								<Input
									id="fpl-name"
									value={name}
									invalid={Boolean(errors.name)}
									onChange={e => {
										setName(e.target.value);
										setErrors(x => ({ ...x, name: undefined }));
									}}
									placeholder="e.g. Turns & Pirouettes"
								/>
								{errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
							</FormGroup>
							<FormGroup>
								<Label for="fpl-desc">Description</Label>
								<Input
									id="fpl-desc"
									type="textarea"
									rows={3}
									value={description}
									onChange={e => setDescription(e.target.value)}
								/>
							</FormGroup>
							<FormGroup>
								<Label>Thumbnail</Label>
								<div className="fpl-thumb-uploader">
									<ImageUploader label="Choose image" value={thumbnail || null} handleImageChange={file => setThumbnail(file)} />
									{thumbnail && (
										<Button size="sm" color="link" className="text-danger p-0" onClick={() => setThumbnail('')}>
											Remove thumbnail
										</Button>
									)}
								</div>
							</FormGroup>
						</CardBody>
					</Card>

					<Card className="fpl-card mt-3">
						<CardHeader>
							<CardTitle tag="h5" className="mb-0">
								Release
							</CardTitle>
						</CardHeader>
						<CardBody>
							<FormGroup check>
								<Input type="radio" id="fpl-live" checked={!scheduled} onChange={() => setScheduled(false)} />
								<Label check for="fpl-live">
									Live now: members see it as soon as it's saved
								</Label>
							</FormGroup>
							<FormGroup check className="mb-2">
								<Input type="radio" id="fpl-sched" checked={scheduled} onChange={() => setScheduled(true)} />
								<Label check for="fpl-sched">
									Schedule a release date
								</Label>
							</FormGroup>

							{scheduled && (
								<div className="fpl-schedule">
									<Label className="small text-muted mb-1">Release date and time (Eastern Time)</Label>
									<ReactDatePicker
										selected={releaseDate}
										onChange={date => {
											setReleaseDate(date);
											setErrors(x => ({ ...x, release: undefined }));
										}}
										showTimeSelect
										timeIntervals={15}
										dateFormat="MMMM d, yyyy h:mm aa"
										className="form-control"
										wrapperClassName="w-100"
									/>
									{errors.release && <div className="invalid-feedback d-block">{errors.release}</div>}
								</div>
							)}

							<div className="mt-3 small">
								<span className="me-2">After saving:</span>
								<ReleaseBadge releaseDateTime={releaseIso} />
								{releaseIso && isReleased(releaseIso) && (
									<div className="text-muted mt-1">This time is in the past, so the playlist will be live immediately.</div>
								)}
								{isEdit && originalRelease !== releaseIso && (
									<div className="text-muted mt-1">
										Currently: {originalRelease ? formatReleaseET(originalRelease) : 'live (no release date)'}
									</div>
								)}
							</div>
						</CardBody>
					</Card>
				</Col>

				<Col lg="7">
					<Card className="fpl-card">
						<CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
							<CardTitle tag="h5" className="mb-0">
								Videos in this playlist
							</CardTitle>
							<small className="text-muted">
								{videos.length} video{videos.length === 1 ? '' : 's'} · {formatDuration(totalDuration)} total
							</small>
						</CardHeader>
						<CardBody>
							{errors.videos && <Alert color="danger" className="py-2">{errors.videos}</Alert>}
							{videos.some(v => v.unavailable) && (
								<Alert color="warning" className="py-2 small">
									Some videos were deleted or deactivated. Members can't play them, so remove them from the playlist.
								</Alert>
							)}
							{videos.length === 0 ? (
								<div className="text-center text-muted py-4">Search below and add videos. Drag to set the order.</div>
							) : (
								<ReorderPlaylistComponent
									items={videos.map((v, i) => ({ ...v, id: v._id, position: i }))}
									onReorder={next => setVideos(next)}
									renderItem={(item, index) => renderSelected(item, index)}
									gap={6}
								/>
							)}
						</CardBody>
					</Card>

					<Card className="fpl-card mt-3">
						<CardHeader>
							<CardTitle tag="h5" className="mb-0">
								Add videos
							</CardTitle>
						</CardHeader>
						<CardBody>
							<div className="fpl-search mb-2">
								<Search size={16} />
								<Input
									type="search"
									placeholder="Search by video title or instructor"
									value={query}
									onChange={e => setQuery(e.target.value)}
								/>
								{searching && <Spinner size="sm" />}
							</div>
							{!query.trim() && <div className="small text-muted mb-2">Showing the most recent videos.</div>}
							<div className="fpl-results">
								{results.length === 0 && !searching ? (
									<div className="text-center text-muted py-3">No videos found.</div>
								) : (
									results.map(video => {
										const added = selectedIds.has(video._id);
										return (
											<div className="fpl-vrow" key={video._id}>
												<VideoThumb video={video} />
												<div className="fpl-main">
													<div className="fpl-vtitle">{video.title || '(untitled video)'}</div>
													<VideoMeta video={video} />
												</div>
												<Button
													size="sm"
													color={added ? 'secondary' : 'primary'}
													outline={added}
													disabled={added}
													onClick={() => addVideo(video)}
												>
													{added ? (
														'Added'
													) : (
														<>
															<Plus size={14} className="me-1" />
															Add
														</>
													)}
												</Button>
											</div>
										);
									})
								)}
							</div>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</div>
	);
}

export default FeaturedPlaylistEditor;
